import { useAuth } from '@wui/common-library'
import { AxiosInstance } from 'axios'

import { useLogoutHook } from '@/features/logout'

import { useAxios } from '@/shared/composables/axios'
import { getPageIdForRequest } from '@/shared/utils/page-id'

import { appBaseUrl, tauriMode } from './import-meta'
import { dynamicallyImportedInvoke } from './tauri'

declare type RequestHeaders = Omit<
  HeadersInit,
  'query' | 'body' | 'timeout' | 'responseType'
>

enum ResponseType {
  JSON = 1,
  Text = 2,
  Binary = 3,
}

export interface Request extends RequestInit {
  query?: Record<string, unknown>
  params?: Record<string, unknown>
  timeout?: number
  responseType?: ResponseType | string
}

export interface FetchResponse<T = unknown> {
  data: T
  status: number
  statusText: string
  headers: unknown
}

export interface IFetchAPIAxios extends Omit<AxiosInstance, 'defaults' | 'interceptors' | 'getUri' | 'request' | 'head' | 'options' | 'postForm' | 'putForm' | 'patchForm' | 'query'> {
  baseUrl: string;
  head(url: string, options?: Request): Promise<FetchResponse<unknown>>;
}

export interface IFetchAPI {
  baseUrl: string
  head<T = unknown, R = FetchResponse<T>>(
    url: string,
    options?: Request
  ): Promise<R>
  post<T = unknown, R = FetchResponse<T>>(
    url: string,
    data?:  object | unknown,
    options?: Request
  ): Promise<R>
  get<T = unknown, R = FetchResponse<T>>(
    url: string,
    options?: Request
  ): Promise<R>
  put<T = unknown, R = FetchResponse<T>>(
    url: string,
    data: object | unknown,
    options?: Request
  ): Promise<R>
  delete<T = unknown, R = FetchResponse<T>>(
    url: string,
    options?: unknown
  ): Promise<R>
  patch<T = unknown, R = FetchResponse<T>>(
    url: string,
    data?: object | unknown,
    options?: Request
  ): Promise<R>
}

export async function readFile (
  file: File,
): Promise<[Uint8Array, string, string]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () =>
      resolve([
        new Uint8Array(reader.result as ArrayBuffer),
        file.type,
        file.name,
      ])
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

const parseQueryParams = (query: Record<string, string>) => {
  if (query) {
    return (Object.keys(query))
      .map((key) => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(query[key] as string)
      })
      .join('&')
  }

  return ''
}

export const getCorrectUrl = (
  baseURL: string,
  url: string,
  queryParams?: Record<string, unknown>,
) => {
  let fullUrl = url.startsWith('https://') ? url : `${baseURL}${url}`
  if (queryParams) {
    const query = parseQueryParams(queryParams as Record<string, string>)
    fullUrl += `?${query}`
  }
  return fullUrl
}

export const getNormalData = async (data: Body, type: string) => {
  const normalizedType = type.toLowerCase().split(';')[0]?.trim() ?? ''

  if (normalizedType === 'text/json') {
    const text = await data.text()
    if (!text.trim()) {
      return null
    }

    try {
      return JSON.parse(text)
    } catch {
      return null
    }
  }

  if (normalizedType.startsWith('text')) {
    const text = await data.text()
    const trimmed = text.trim()

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.parse(trimmed)
      } catch {
        return text
      }
    }

    return text
  }

  if (normalizedType.startsWith('application/json') || normalizedType === '') {
    const text = await data.text()
    if (!text.trim()) {
      return null
    }

    try {
      return JSON.parse(text)
    } catch {
      return normalizedType.startsWith('application/json') ? null : text
    }
  }

  return await data.blob()
}

export class FetchApi implements IFetchAPI {
  baseUrl: string
  constructor (url: string) {
    this.baseUrl = url
  }

  static of (baseUrl: string, instance: AxiosInstance) : IFetchAPIAxios {
    return {
      baseUrl,
      create: instance.create,
      get: instance.get,
      head: (url: string, options?: Request) => {
        const queryParams = options?.params ?? options?.query
        return instance.head(url, { params: queryParams as Record<string, string> })
          .then((res) => ({
            data: null,
            status: res.status,
            statusText: res.statusText,
            headers: res.headers,
          })) as Promise<FetchResponse<unknown>>
      },
      post: instance.post,
      delete: instance.delete,
      patch: instance.patch,
      put: instance.put,
    }
  }

  async post<T = unknown, R = FetchResponse<T>> (
    url: string,
    data?: object | unknown,
    options?: Request,
  ): Promise<R> {
    try {
      const queryParams = options?.params ? options?.params : options?.query
      const bodyRequest = data instanceof FormData ? data : JSON.stringify(data) || null
      const hasJsonBody = !(data instanceof FormData) && bodyRequest != null

      const { fetch } = await import('@tauri-apps/plugin-http')
      const request = await fetch(getCorrectUrl(this.baseUrl, url, queryParams), {
        method: 'POST',
        headers: buildRequestHeaders(options?.headers, hasJsonBody),
        body: bodyRequest,
      })

      if (!request.ok) {
        if (request.status === 401) {
          const { logout } = useLogoutHook()
          logout()
        }
        throw new Error(request.statusText)
      }

      const requestData = await getNormalData(
        request,
        request.headers.get('Content-Type') || '',
      )

      const response = {
        data: requestData as T,
        status: request.status,
        statusText: request.statusText,
        headers: request.headers,
      } as R

      return response
    } catch (error) {
      throw error
    }
  }

  async head<T = unknown, R = FetchResponse<T>> (
    url: string,
    options?: Request,
  ): Promise<R> {
    try {
      const queryParams = options?.params ? options?.params : options?.query
      const { fetch } = await import('@tauri-apps/plugin-http')
      const request = await fetch(getCorrectUrl(this.baseUrl, url, queryParams), {
        method: 'HEAD',
        headers: buildRequestHeaders(options?.headers),
      })

      if (!request.ok) {
        if (request.status === 401) {
          const { logout } = useLogoutHook()
          logout()
        }
        throw new Error(request.statusText)
      }

      const response = {
        data: null as T,
        status: request.status,
        statusText: request.statusText,
        headers: request.headers,
      } as R

      return response
    } catch (error) {
      throw error
    }
  }

  async get<T = unknown, R = FetchResponse<T>> (
    url: string,
    options?: Request,
  ): Promise<R> {
    try {
      const queryParams = options?.params ? options?.params : options?.query
      const { fetch } = await import('@tauri-apps/plugin-http')
      const request = await fetch(getCorrectUrl(this.baseUrl, url, queryParams), {
        method: 'GET',
        headers: buildRequestHeaders(options?.headers),
      })

      if (!request.ok) {
        if (request.status === 401) {
          const { logout } = useLogoutHook()
          logout()
        }
        throw new Error(request.statusText)
      }

      const requestData = await getNormalData(
        request,
        request.headers.get('Content-Type') || '',
      )

      const response = {
        data: requestData as T,
        status: request.status,
        statusText: request.statusText,
        headers: request.headers,
      } as R

      return response
    } catch (error) {
      throw error
    }
  }

  async put<T = unknown, R = FetchResponse<T>> (
    url: string,
    data: object | unknown,
    options?: Request,
  ): Promise<R> {
    try {
      const queryParams = options?.params ? options?.params : options?.query

      const bodyRequest = data instanceof FormData ? data : JSON.stringify(data) || null
      const hasJsonBody = !(data instanceof FormData) && bodyRequest != null

      const { fetch } = await import('@tauri-apps/plugin-http')
      const request = await fetch(getCorrectUrl(this.baseUrl, url, queryParams), {
        method: 'PUT',
        headers: buildRequestHeaders(options?.headers, hasJsonBody),
        body: bodyRequest,
      })

      if (!request.ok) {
        if (request.status === 401) {
          const { logout } = useLogoutHook()
          logout()
        }
        throw new Error(request.statusText)
      }

      const requestData = await getNormalData(
        request,
        request.headers.get('Content-Type') || '',
      )

      const response = {
        data: requestData as T,
        status: request.status,
        statusText: request.statusText,
        headers: request.headers,
      } as R

      return response
    } catch (error) {
      throw error
    }
  }

  async delete<T = unknown, R = FetchResponse<T>> (
    url: string,
    options?: Request,
  ): Promise<R>{
    try {
      const queryParams = options?.params ? options?.params : options?.query
      const { fetch } = await import('@tauri-apps/plugin-http')
      const request = await fetch(getCorrectUrl(this.baseUrl, url, queryParams), {
        method: 'DELETE',
        headers: buildRequestHeaders(options?.headers),
      })

      if (!request.ok) {
        if (request.status === 401) {
          const { logout } = useLogoutHook()
          logout()
        }
        throw new Error(request.statusText)
      }

      const requestData = await getNormalData(
        request,
        request.headers.get('Content-Type') || '',
      )

      const response = {
        data: requestData as T,
        status: request.status,
        statusText: request.statusText,
        headers: request.headers,
      } as R

      return response
    } catch (error) {
      throw error
    }
  }

  async patch<T = unknown, R = FetchResponse<T>> (
    url: string,
    data?: object | unknown,
    options?: Request,
  ): Promise<R> {
    try {
      const queryParams = options?.params ? options?.params : options?.query
      const bodyRequest = data instanceof FormData ? data : JSON.stringify(data) || null
      const hasJsonBody = !(data instanceof FormData) && bodyRequest != null
      const { fetch } = await import('@tauri-apps/plugin-http')
      const request = await fetch(getCorrectUrl(this.baseUrl, url, queryParams), {
        method: 'PATCH',
        headers: buildRequestHeaders(options?.headers, hasJsonBody),
        body: bodyRequest,
      })

      if (!request.ok) {
        if (request.status === 401) {
          const { logout } = useLogoutHook()
          logout()
        }
        throw new Error(request.statusText)
      }

      const requestData = await getNormalData(
        request,
        request.headers.get('Content-Type') || '',
      )

      const response = {
        data: requestData as T,
        status: request.status,
        statusText: request.statusText,
        headers: request.headers,
      } as R

      return response
    } catch (error) {
      throw error
    }
  }
}

const JSON_REQUEST_HEADERS = {
  Accept: 'application/json, text/plain',
  'Content-Type': 'application/json;charset=UTF-8',
} as const

function buildRequestHeaders (
  headers: RequestHeaders | HeadersInit | undefined,
  withJsonBody = false,
) {
  const auth = useAuth()
  const jwtToken = auth.token.value || ''

  const pageId = getPageIdForRequest()

  return Object.entries({
    Accept: JSON_REQUEST_HEADERS.Accept,
    ...(withJsonBody ? { 'Content-Type': JSON_REQUEST_HEADERS['Content-Type'] } : {}),
    ...headers,
    ...(jwtToken && getJWTAuthorization(jwtToken)),
    pageId,
  }).filter(([, value]) => value !== undefined).map(([key, value]) => [key, String(value)]) as [string, string][]
}

function getJWTAuthorization (jwt: string) {
  return {
    Authorization: `Bearer ${jwt}`,
  }
}

const toTauriHeadersMap = (headers: [string, string][]) => {
  return new Map(headers.map(([key, value]) => [key, value]))
}

export async function uploadFile (
  url: string,
  path: string,
  method: string,
  requestOptions?: Request,
): Promise<void> {
  const options = {
    body: requestOptions?.body,
    headers: buildRequestHeaders(requestOptions?.headers),
    query: requestOptions?.query,
    timeout: requestOptions?.timeout,
    responseType: requestOptions?.responseType,
  }

  const ids = new Uint32Array(1)
  window.crypto.getRandomValues(ids)
  const id = ids[0]

  const baseUrl = appBaseUrl()
  return await dynamicallyImportedInvoke('upload_file', {
    id: id,
    url: `${baseUrl}/${url}`,
    path: path,
    methods: method,
    headers: toTauriHeadersMap(options.headers),
    query: options.query,
  }, true)
}

export async function downloadFile (
  url: string,
  dest: string,
  requestOptions?: Request,
): Promise<void> {
  const options = {
    body: requestOptions?.body,
    headers: buildRequestHeaders(requestOptions?.headers),
    query: requestOptions?.query,
    timeout: requestOptions?.timeout,
    responseType: requestOptions?.responseType,
  }

  const ids = new Uint32Array(1)
  window.crypto.getRandomValues(ids)
  const id = ids[0]

  const baseUrl = appBaseUrl()

  return await dynamicallyImportedInvoke('download_file', {
    id: id,
    url: `${baseUrl}/${url}`,
    path: `${dest}`,
    headers: toTauriHeadersMap(options.headers),
  }, true)
}

export async function upload (
  url: string,
  path: string,
  method: string,
  requestOptions?: Request,
) {
  const options = {
    body: requestOptions?.body,
    headers: buildRequestHeaders(requestOptions?.headers),
    query: requestOptions?.query,
    timeout: requestOptions?.timeout,
    responseType: requestOptions?.responseType,
  }
  const ids = new Uint32Array(1)
  window.crypto.getRandomValues(ids)
  const id = ids[0]

  const baseUrl = appBaseUrl()

  return await dynamicallyImportedInvoke('upload', {
    id: id,
    url: `${baseUrl}/${url}`,
    path: path,
    methods: method,
    headers: toTauriHeadersMap(options.headers),
    query: options.query,
  })
}

export function useFetchApi (): IFetchAPI {
  const createRequestInstance = (baseUrl: string): IFetchAPI => {
    if (tauriMode()) {
      return new FetchApi(baseUrl)
    } else {
      const { axiosInstance } = useAxios()
      return FetchApi.of(baseUrl, axiosInstance) as IFetchAPI
    }
  }

  return createRequestInstance(appBaseUrl())
}
